import { useState, useMemo } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, StyleSheet,
  ActivityIndicator, TextInput,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useFinancialOverview } from '@/hooks/useFinancialOverview';
import { PaymentStatus, StudentPaymentStatus } from '@/lib/types';

const STATUS_COLOR: Record<PaymentStatus, string> = {
  paid:    '#2E7D32',
  pending: '#F9A825',
  overdue: '#C8102E',
  no_fee:  '#888',
};
const STATUS_LABEL: Record<PaymentStatus, string> = {
  paid:    'Pago',
  pending: 'Pendente',
  overdue: 'Vencido',
  no_fee:  'Avulso',
};
const MONTH_NAMES = [
  'Janeiro','Fevereiro','Março','Abril','Maio','Junho',
  'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro',
];

function fmtBRL(v: number): string {
  return `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

type StatusFilter = 'todos' | PaymentStatus;

export function FinancialDesktop({ personalId }: { personalId: string }) {
  const router = useRouter();
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('todos');
  const [search, setSearch] = useState('');

  const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth();

  const { overview, projected, received, pendingAmount, loading } =
    useFinancialOverview(personalId, { year: selectedYear, month: selectedMonth });

  function prevMonth() {
    if (selectedMonth === 0) { setSelectedYear(y => y - 1); setSelectedMonth(11); }
    else setSelectedMonth(m => m - 1);
  }
  function nextMonth() {
    if (isCurrentMonth) return;
    if (selectedMonth === 11) { setSelectedYear(y => y + 1); setSelectedMonth(0); }
    else setSelectedMonth(m => m + 1);
  }

  // Cálculos extras pros KPIs
  const overdueAmount = useMemo(
    () => overview.filter(o => o.status === 'overdue').reduce((s, o) => s + (o.student.monthly_fee ?? 0), 0),
    [overview]
  );
  const paidCount = useMemo(() => overview.filter(o => o.status === 'paid').length, [overview]);
  const totalStudents = overview.length;

  const filteredOverview = useMemo(() => {
    const q = search.toLowerCase().trim();
    return overview.filter(o => {
      if (statusFilter !== 'todos' && o.status !== statusFilter) return false;
      if (q) {
        const name = (o.student.full_name ?? o.student.email ?? '').toLowerCase();
        if (!name.includes(q)) return false;
      }
      return true;
    });
  }, [overview, statusFilter, search]);

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>

      {/* Header: título + navegação de mês */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Financeiro</Text>
          <Text style={styles.subtitle}>Mensalidades e pagamentos</Text>
        </View>

        <View style={styles.monthNav}>
          <TouchableOpacity onPress={prevMonth} style={styles.monthBtn} hitSlop={{ top:8, bottom:8, left:8, right:8 }}>
            <Ionicons name="chevron-back" size={18} color="#1A1A1A" />
          </TouchableOpacity>
          <Text style={styles.monthLabel}>{MONTH_NAMES[selectedMonth]} {selectedYear}</Text>
          <TouchableOpacity
            onPress={nextMonth}
            disabled={isCurrentMonth}
            style={[styles.monthBtn, isCurrentMonth && { opacity: 0.3 }]}
            hitSlop={{ top:8, bottom:8, left:8, right:8 }}
          >
            <Ionicons name="chevron-forward" size={18} color="#1A1A1A" />
          </TouchableOpacity>
        </View>
      </View>

      {/* KPIs */}
      <View style={styles.kpiRow}>
        <Kpi label="Recebido" value={fmtBRL(received)} color="#2E7D32" sub={`${paidCount}/${totalStudents} pagaram`} />
        <Kpi label="Projetado" value={fmtBRL(projected)} sub="se todos pagarem" />
        <Kpi label="Em aberto" value={fmtBRL(pendingAmount)} color="#F9A825" sub="ainda no prazo" />
        <Kpi label="Vencido" value={fmtBRL(overdueAmount)} color="#C8102E" sub="já passou do dia" />
      </View>

      {/* Filtros */}
      <View style={styles.toolbar}>
        <View style={styles.searchBox}>
          <Ionicons name="search-outline" size={14} color="#888" />
          <TextInput
            style={styles.searchInput}
            placeholder="Buscar por nome ou email…"
            placeholderTextColor="#aaa"
            value={search}
            onChangeText={setSearch}
          />
        </View>
        <View style={styles.filterChips}>
          <Chip label={`Todos (${totalStudents})`} active={statusFilter === 'todos'} onPress={() => setStatusFilter('todos')} />
          {(['paid','pending','overdue','no_fee'] as PaymentStatus[]).map(st => (
            <Chip
              key={st}
              label={`${STATUS_LABEL[st]} (${overview.filter(o => o.status === st).length})`}
              active={statusFilter === st}
              color={STATUS_COLOR[st]}
              onPress={() => setStatusFilter(st)}
            />
          ))}
        </View>
      </View>

      {/* Tabela */}
      <View style={styles.table}>
        <View style={styles.thead}>
          <Text style={[styles.th, { flex: 3 }]}>Aluno</Text>
          <Text style={[styles.th, { flex: 1.4, textAlign: 'right' }]}>Mensalidade</Text>
          <Text style={[styles.th, { flex: 1, textAlign: 'center' }]}>Vence dia</Text>
          <Text style={[styles.th, { flex: 1.4, textAlign: 'center' }]}>Status</Text>
          <Text style={[styles.th, { flex: 1.4, textAlign: 'center' }]}>Pago em</Text>
          <Text style={[styles.th, { flex: 0.6, textAlign: 'right' }]}></Text>
        </View>

        {loading ? (
          <ActivityIndicator color="#C8102E" style={{ padding: 40 }} />
        ) : filteredOverview.length === 0 ? (
          <Text style={styles.empty}>Nenhum aluno com esses filtros.</Text>
        ) : (
          filteredOverview.map((item, idx) => (
            <Row
              key={item.student.id}
              item={item}
              zebra={idx % 2 === 1}
              onPress={() => router.push(`/(personal)/students/${item.student.id}` as never)}
            />
          ))
        )}
      </View>
    </ScrollView>
  );
}

// ─── Sub-componentes ───────────────────────────────────────────────────────

function Kpi({ label, value, color, sub }: { label: string; value: string; color?: string; sub?: string }) {
  return (
    <View style={styles.kpi}>
      <Text style={styles.kpiLabel}>{label}</Text>
      <Text style={[styles.kpiValue, color && { color }]}>{value}</Text>
      {sub && <Text style={styles.kpiSub}>{sub}</Text>}
    </View>
  );
}

function Chip({ label, active, color, onPress }:
  { label: string; active: boolean; color?: string; onPress: () => void }) {
  return (
    <TouchableOpacity
      style={[styles.chip, active && { backgroundColor: color ?? '#C8102E' }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text style={[styles.chipText, active && { color: '#fff' }]}>{label}</Text>
    </TouchableOpacity>
  );
}

function Row({ item, zebra, onPress }: { item: StudentPaymentStatus; zebra: boolean; onPress: () => void }) {
  const { student, status, currentMonthPayment } = item;
  return (
    <TouchableOpacity
      style={[styles.tr, zebra && { backgroundColor: '#FAFAFA' }]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={[styles.td, { flex: 3 }]}>
        <Text style={styles.cellName}>{student.full_name ?? student.email}</Text>
        {student.full_name && <Text style={styles.cellSub}>{student.email}</Text>}
      </View>
      <Text style={[styles.td, styles.cellMoney, { flex: 1.4, textAlign: 'right' }]}>
        {student.monthly_fee != null ? fmtBRL(student.monthly_fee) : '—'}
      </Text>
      <Text style={[styles.td, styles.cellText, { flex: 1, textAlign: 'center' }]}>
        {student.due_day ?? '—'}
      </Text>
      <View style={[styles.td, { flex: 1.4, alignItems: 'center' }]}>
        <View style={[styles.badge, { backgroundColor: STATUS_COLOR[status] + '22' }]}>
          <Text style={[styles.badgeText, { color: STATUS_COLOR[status] }]}>{STATUS_LABEL[status]}</Text>
        </View>
      </View>
      <Text style={[styles.td, styles.cellText, { flex: 1.4, textAlign: 'center' }]}>
        {currentMonthPayment
          ? new Date(currentMonthPayment.paid_at).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
          : '—'}
      </Text>
      <View style={[styles.td, { flex: 0.6, alignItems: 'flex-end' }]}>
        <Ionicons name="chevron-forward" size={16} color="#aaa" />
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  content: { padding: 24, paddingBottom: 48 },

  header: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  title: { fontSize: 22, fontWeight: '800', color: '#1A1A1A' },
  subtitle: { fontSize: 13, color: '#888', marginTop: 2 },

  monthNav: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: '#fff', borderWidth: 1, borderColor: '#EEE',
    paddingHorizontal: 8, paddingVertical: 6, borderRadius: 8,
  },
  monthBtn: { padding: 4 },
  monthLabel: { fontSize: 14, fontWeight: '600', color: '#1A1A1A', minWidth: 130, textAlign: 'center' },

  kpiRow: { flexDirection: 'row', gap: 12, marginBottom: 16 },
  kpi: {
    flex: 1, backgroundColor: '#fff', borderRadius: 10, padding: 14,
    borderWidth: 1, borderColor: '#EEE',
  },
  kpiLabel: { fontSize: 11, color: '#888', fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  kpiValue: { fontSize: 20, fontWeight: '800', color: '#1A1A1A' },
  kpiSub: { fontSize: 10, color: '#aaa', marginTop: 4 },

  toolbar: {
    flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 12,
    flexWrap: 'wrap',
  },
  searchBox: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    backgroundColor: '#fff', borderWidth: 1, borderColor: '#EEE',
    paddingHorizontal: 12, paddingVertical: 8, borderRadius: 8,
    minWidth: 240, flex: 1,
  },
  searchInput: { flex: 1, fontSize: 13, color: '#1A1A1A', outlineStyle: 'none' as never },

  filterChips: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip: { paddingHorizontal: 12, paddingVertical: 6, borderRadius: 16, backgroundColor: '#fff', borderWidth: 1, borderColor: '#EEE' },
  chipText: { fontSize: 12, color: '#555', fontWeight: '600' },

  table: { backgroundColor: '#fff', borderRadius: 10, overflow: 'hidden', borderWidth: 1, borderColor: '#EEE' },
  thead: {
    flexDirection: 'row', alignItems: 'center', gap: 12,
    paddingHorizontal: 16, paddingVertical: 12,
    backgroundColor: '#FAFAFA', borderBottomWidth: 1, borderBottomColor: '#EEE',
  },
  th: { fontSize: 11, fontWeight: '700', color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 },

  tr: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F2F2F2' },
  td: {},
  cellName: { fontSize: 13, fontWeight: '600', color: '#1A1A1A' },
  cellSub: { fontSize: 11, color: '#888', marginTop: 2 },
  cellText: { fontSize: 13, color: '#555' },
  cellMoney: { fontSize: 13, color: '#1A1A1A', fontWeight: '600' },

  badge: { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 10 },
  badgeText: { fontSize: 11, fontWeight: '700' },

  empty: { fontSize: 13, color: '#888', textAlign: 'center', padding: 40 },
});
